import { z } from 'zod'
import { changedOnSchema } from './change-made.input.js'
import { uuidSchema } from '../validators/uuid.validator.js'
import { QUANTITY_PATTERN } from '../helpers/quantity.js'
import { MONEY_AMOUNT_PATTERN } from '../helpers/money.js'
import { MAX_ESTIMATED_MINUTES } from '../helpers/minutes.js'
import { problemSeveritySchema } from '../enums/problem-severity.enum.js'
import { JOB_TYPES, jobLevelSchema, jobTypeSchema } from '../enums/job-type.enum.js'
import {
  accuracyMetersInputSchema,
  geoPointSchema,
} from '../dto/geo.dto.js'
import { positionSourceSchema } from '../enums/position-source.enum.js'
import { seasonSchema } from '../enums/season.enum.js'

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? null : value))
    .nullish()

/**
 * The jobs on a tree, as a set.
 *
 * Bounded by the number of job types that exist and rejected outright when a
 * type repeats, mirroring the `(treeId, type)` primary key. Letting a duplicate
 * through would mean the database silently keeps whichever arrived last, and
 * "Crown Cleaning Level 1 and Crown Cleaning Level 3" is a data-entry mistake
 * worth a 400 rather than a coin flip.
 */
const jobsSchema = z
  .array(
    z
      .object({
        type: jobTypeSchema,
        level: jobLevelSchema,
        /**
         * Crew minutes for this job, when the tree's time has been split up.
         *
         * Null is "not itemized" and is the ordinary case. Itemizing is what
         * makes a job type declinable by the client — without a figure there
         * is nothing to take away, so the work stays in the price.
         */
        minutes: z.number().int().nonnegative().max(MAX_ESTIMATED_MINUTES).nullish(),
        /** What the OTHER work is. Required once OTHER carries minutes. */
        notes: optionalText(500),
      })
      .strict(),
  )
  .max(JOB_TYPES.length)
  .refine(
    (jobs) => new Set(jobs.map((job) => job.type)).size === jobs.length,
    'A tree cannot carry the same job type twice',
  )
  /*
   * All of the tree's jobs carry minutes, or none of them do.
   *
   * Half-itemized would mean the parts do not add up to the whole, so the
   * tree's total could not be the sum — and a client declining one job would
   * be taking away a figure that was never part of what they were charged.
   */
  .refine((jobs) => {
    const itemized = jobs.filter(
      (job) => job.minutes !== null && job.minutes !== undefined,
    ).length
    return itemized === 0 || itemized === jobs.length
  }, 'Give every job on this tree a time, or none of them')
  /*
   * The one line a client cannot decline has to say what it is. An unlabelled
   * charge nobody may argue with is the worst thing to put before a board.
   */
  .refine(
    (jobs) =>
      jobs.every(
        (job) =>
          job.type !== 'OTHER' ||
          job.minutes === null ||
          job.minutes === undefined ||
          job.minutes === 0 ||
          (job.notes !== null && job.notes !== undefined),
      ),
    { message: 'Say what the other work is', path: ['notes'] },
  )

/**
 * A latitude/longitude accepted from a client.
 *
 * Range-checked but not precision-checked: the column rounds to six decimal
 * places (~0.11 m) on write, and rejecting a device that reports fifteen would
 * be refusing a perfectly good fix for being too detailed.
 */
/**
 * Estimated labor in whole minutes.
 *
 * Bounded by `MAX_ESTIMATED_MINUTES`, which is sixteen hours: past a single
 * crew's long day on one tree, and the point at which a number is far more
 * likely to be hours typed where minutes go — or a stray digit — than a plan.
 * Letting one through would quietly wreck a site total and the quote built on
 * it.
 */
const estimatedLaborMinutesSchema = z
  .number()
  .int('Estimated labor must be a whole number of minutes')
  .nonnegative()
  .max(
    MAX_ESTIMATED_MINUTES,
    `Estimated labor must be ${String(MAX_ESTIMATED_MINUTES)} minutes or less`,
  )
  .nullish()

/**
 * The problems on a tree, as a complete replacement set.
 *
 * Whole-list semantics, not a diff, because the survey replays an offline
 * queue where the last write for a tree is simply correct — the same
 * last-write-wins rule the sync endpoint already applies to jobs. A diff would
 * need the client and server to agree on what was there before, which is
 * exactly what a phone that has been offline all morning cannot promise.
 *
 * `id` is supplied by the client for the same reason a tree's is: a problem
 * recorded in a park has to have an identity before it has ever reached a
 * server, or replaying the queue would insert it twice.
 */
/**
 * A quantity a surveyor types: feet of cable, inches of trunk.
 *
 * A string so the exact decimal reaches the `Decimal` column without passing
 * through a float, the same treatment money gets. Bounded at four digits and
 * two places, which holds any tree and rejects a mistyped one.
 */
const quantitySchema = z
  .string()
  .trim()
  // The shared pattern, not a second copy of it. A client willing to commit a
  // value the server rejects wedges the whole sync batch, so the two ends
  // agreeing is load-bearing rather than tidy.
  .regex(
    new RegExp(`^$|${QUANTITY_PATTERN.source}`),
    'Enter a number, e.g. 12 or 12.5',
  )
  .transform((value) => (value === '' ? null : value))
  .nullish()

/**
 * A price the arborist set by hand, in dollars, replacing what the formula
 * works out to (docs/proposals.md, "A price can be set by hand").
 *
 * A string to the `Decimal` column, like every other amount. Empty clears it
 * back to the formula, and absent leaves it alone, so a phone on a build that
 * has never heard of overrides cannot clear one a newer phone set.
 */
const priceOverrideSchema = z
  .string()
  .trim()
  .regex(
    new RegExp(`^$|${MONEY_AMOUNT_PATTERN.source}`),
    'Enter a price in dollars, e.g. 850 or 850.50',
  )
  .transform((value) => (value === '' ? null : value))
  .nullish()

/**
 * The solutions chosen for one problem, as a complete replacement set.
 *
 * Whole-list, like the problems that carry them and for the same reason: a
 * phone replaying a morning of offline work knows what it decided, not what
 * the server thought before it.
 *
 * No DBH here. Diameter is a fact about the tree and lives on the tree; a
 * solution priced by it reads it there.
 */
const problemSolutionsSchema = z
  .array(
    z
      .object({
        solutionId: uuidSchema,
        minutes: z.number().int().nonnegative().max(100_000).nullish(),
        linearFeet: quantitySchema,
        /**
         * When it should be done. Left out, as by a build from before
         * seasons, a treatment keeps the season it has, so an older phone's
         * sync cannot clear one a newer phone chose.
         */
        season: seasonSchema.nullish(),
      })
      .strict(),
  )
  .max(20)
  .refine((solutions) => {
    const ids = solutions.map((solution) => solution.solutionId)
    return new Set(ids).size === ids.length
  }, 'A problem cannot carry the same solution twice')

const problemsSchema = z
  .array(
    z
      .object({
        id: uuidSchema,
        typeId: uuidSchema.nullable(),
        notes: optionalText(2000),
        severity: problemSeveritySchema.nullish(),
        /**
         * When it is dealt with. Left out, as by a build from before
         * problems had seasons, a problem keeps the season it has, so an
         * older phone's sync cannot clear one a newer phone chose.
         */
        season: seasonSchema.nullish(),
        /** Sent whole, replacing whatever this problem carried. */
        solutions: problemSolutionsSchema.default([]),
        /**
         * What dealing with it costs on this tree, set by hand in place of
         * its treatments' pricing. Null clears it; absent leaves it alone.
         */
        priceOverride: priceOverrideSchema,
        /**
         * The problem on an earlier visit to this tree it was brought
         * forward from ("Still here"). Kept only when it names one; left
         * out, a problem keeps the link it has.
         */
        previousId: uuidSchema.nullish(),
      })
      .strict()
      /*
       * "Other" is the only case where the note carries the whole meaning. A
       * catalogued problem is already described by its directory entry, so an
       * empty note there is normal; an empty "Other" is a row that says
       * nothing at all, and would render as a blank line in a proposal.
       */
      .refine(
        (problem) =>
          problem.typeId !== null ||
          (problem.notes !== null && problem.notes !== undefined),
        { message: 'Describe the problem', path: ['notes'] },
      ),
  )
  .max(20)
  .refine((problems) => {
    const typeIds = problems
      .map((problem) => problem.typeId)
      .filter((typeId): typeId is string => typeId !== null)
    return new Set(typeIds).size === typeIds.length
  }, 'A tree cannot carry the same problem twice')

const latitudeSchema = geoPointSchema.shape.latitude
const longitudeSchema = geoPointSchema.shape.longitude

/**
 * The writable fields of a tree, WITHOUT create-time defaults.
 *
 * The split matters. `.partial()` does not remove a `.default()` — it wraps the
 * field so the key may be absent, and Zod then supplies the default anyway. A
 * partial update built from a defaulted create schema therefore arrives at the
 * service carrying `jobs: []` that the caller never sent, and writing that
 * wipes a tree's whole job list.
 *
 * So defaults are applied only where they belong — on creation, where "we do
 * not know yet" is a real answer — and the update schema is built from this
 * bare set, where an absent key means "leave it alone".
 */
const treeWritableFields = {
  label: z.string().trim().min(1, 'A tree needs a label').max(12),
  /**
   * Null identifies the tree as unidentified, which is a real answer rather
   * than a missing one — so this is `nullable`, and absent still means "leave
   * whatever is there alone".
   */
  speciesId: uuidSchema.nullable(),
  /** Sent whole: the list supplied replaces whatever the tree carried. */
  problems: problemsSchema,
  latitude: latitudeSchema,
  longitude: longitudeSchema,
  accuracyM: accuracyMetersInputSchema.nullish(),
  positionSource: positionSourceSchema,
  estimatedLaborMinutes: estimatedLaborMinutesSchema,
  /**
   * Diameter at breast height, in inches. Null is a real answer — plenty of
   * trees are surveyed without a tape — and solutions priced by it simply do
   * not price until somebody measures.
   */
  dbhInches: quantitySchema,
  /** Whether this tree is quoted for removal as well as, or instead of, pruning. */
  removalOffered: z.boolean().optional(),
  removalMinutes: z
    .number()
    .int()
    .nonnegative()
    .max(MAX_ESTIMATED_MINUTES)
    .nullish(),
  removalNotes: optionalText(2000),
  /**
   * "No pruning recommended": the arborist's answer for a tree that needs
   * none. Optional so an older build can still sync. Sent true, the tree is
   * written with no jobs, no time and no pruning price; sent beside pruning
   * work, the work wins and the flag is cleared (`TreeService`), because
   * dropping a job would quietly shorten a quote.
   */
  noPruningRecommended: z.boolean().optional(),
  /** The pruning price set by hand. Null returns it to the crew rate. */
  pruningPriceOverride: priceOverrideSchema,
  /** The removal price set by hand. Null returns it to the crew rate. */
  removalPriceOverride: priceOverrideSchema,
  /** Why a price was set by hand. Internal; never on a proposal. */
  priceOverrideNotes: optionalText(2000),
  /**
   * Offered but not recommended: the work a board can take or leave. Optional
   * in the input so a phone with an older build, which has never heard of
   * it, can still sync.
   */
  isOptional: z.boolean().optional(),
  notes: optionalText(2000),
  jobs: jobsSchema,
  /**
   * When the pruning (and a quoted removal) should be done. Null clears it;
   * absent, as from a build before seasons, leaves it alone.
   */
  pruningSeason: seasonSchema.nullish(),
}

export const createTreeInputSchema = z
  .object({
    /**
     * Supplied by the CLIENT, not the server.
     *
     * This is what makes offline capture work. A surveyor standing in a park
     * with no signal needs an identity for a tree the moment they drop it — to
     * edit it, to reference it from a queued operation, to avoid creating it
     * twice when the queue is finally replayed. A server-assigned id cannot be
     * handed out until the network is back, so the client generates a UUID and
     * every write becomes an idempotent upsert on it.
     *
     * Optional, so an ordinary online create can still let the server decide.
     */
    id: uuidSchema.optional(),

    ...treeWritableFields,

    // Create-time defaults, deliberately absent from `treeWritableFields`.
    speciesId: uuidSchema.nullable().default(null),
    problems: problemsSchema.default([]),
    positionSource: positionSourceSchema.default('GPS'),
    jobs: jobsSchema.default([]),
  })
  .strict()

export type CreateTreeInput = z.infer<typeof createTreeInputSchema>

export const updateTreeInputSchema = z
  .object(treeWritableFields)
  .partial()
  .strict()

export type UpdateTreeInput = z.infer<typeof updateTreeInputSchema>

/**
 * One queued change from a field device.
 *
 * The queue holds intentions ("this tree should look like this"), not deltas
 * ("change the species"). That is what makes replay safe: a survey phone that
 * loses signal, records eleven edits to the same tree and reconnects an hour
 * later sends the final state, and applying it twice is the same as applying it
 * once. A delta log would have to be applied exactly once, in order, forever.
 */
const upsertOperationSchema = z
  .object({
    op: z.literal('upsert'),
    tree: createTreeInputSchema.extend({ id: uuidSchema }),
    /**
     * The visit the tree's findings go into: its problems, work, time,
     * removal and optional flag. Null when no visit is open, which writes the
     * tree itself and nothing found on it. Left out by a build from before
     * visits, which writes them onto the site's current visit.
     */
    visitId: uuidSchema.nullable().optional(),
    /**
     * The answer to "Was it wrong, or did it change?", asked on a revisit:
     * true for "Missed last time". Left out when nothing asked it.
     */
    isCorrection: z.boolean().optional(),
    /** When it changed, for a change made with no visit open. */
    changedOn: changedOnSchema,
  })
  .strict()

const archiveOperationSchema = z
  .object({
    op: z.literal('archive'),
    id: uuidSchema,
    /** The visit it was taken off in; null for none open. */
    visitId: uuidSchema.nullable().optional(),
    /**
     * True for "Recorded by mistake", false for "Gone". Left out, as by a
     * build from before the question, it was recorded by mistake.
     */
    isCorrection: z.boolean().optional(),
    /** When it went, for one taken off with no visit open. */
    changedOn: changedOnSchema,
  })
  .strict()

export const treeSyncOperationSchema = z.discriminatedUnion('op', [
  upsertOperationSchema,
  archiveOperationSchema,
])

export type TreeSyncOperation = z.infer<typeof treeSyncOperationSchema>

/**
 * A batch of queued changes to reconcile against a site.
 *
 * Capped so one wedged device cannot post a hundred-megabyte body; a survey of
 * a large park is a few hundred trees, and a client with more queued than this
 * should be flushing in batches anyway.
 */
export const syncTreesInputSchema = z
  .object({
    operations: z.array(treeSyncOperationSchema).max(500),
    /**
     * Whose findings the answer shows: the visit this phone is working in, so
     * a visit another phone starts cannot swap the findings under it. Left
     * out, the site's current visit; null, none.
     */
    visitId: uuidSchema.nullable().optional(),
  })
  .strict()

export type SyncTreesInput = z.infer<typeof syncTreesInputSchema>
